<template>
  <div class="dashboard">
    <div v-loading="loading" class="dashboard-inner">
      <!-- 概览卡片 -->
      <div class="stat-grid">
        <div class="stat-card">
          <div class="stat-label">总会员数</div>
          <div class="stat-value">{{ stats.totalMembers || 0 }}</div>
          <div class="stat-sub">活跃 {{ stats.activeMembers || 0 }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">总订单数</div>
          <div class="stat-value">{{ stats.totalOrders || 0 }}</div>
          <div class="stat-sub">已支付 {{ stats.paidOrders || 0 }}</div>
        </div>
        <div class="stat-card accent-revenue">
          <div class="stat-label">总收入</div>
          <div class="stat-value">¥{{ formatPrice(stats.totalRevenue) }}</div>
          <div class="stat-sub">已完成 {{ stats.completedOrders || 0 }}</div>
        </div>
        <div class="stat-card accent-warn">
          <div class="stat-label">待审批提现</div>
          <div class="stat-value">{{ stats.pendingWithdrawals || 0 }}</div>
          <div class="stat-sub">笔</div>
        </div>
      </div>

      <!-- 角色分布 -->
      <section class="panel" v-if="stats.memberByRole">
        <h3 class="panel-title">会员角色分布</h3>
        <div class="role-bars">
          <div class="role-item" v-for="(item, key) in roleDisplay" :key="key">
            <div class="role-label">{{ item.label }}</div>
            <div class="role-bar-wrap">
              <div
                class="role-bar"
                :style="{ width: getRolePercent(item.count) + '%', background: item.color }"
              ></div>
            </div>
            <div class="role-count">{{ item.count }}</div>
          </div>
        </div>
      </section>

      <!-- 收入趋势 -->
      <section class="panel">
        <h3 class="panel-title">近 30 天收入趋势</h3>
        <el-table
          :data="stats.dailyTrend || []"
          size="small"
          max-height="420"
          class="trend-table"
        >
          <el-table-column align="center" label="日期" prop="date" width="120" />
          <el-table-column align="center" label="订单数" prop="orders" width="100" />
          <el-table-column align="center" label="收入" width="140">
            <template #default="{ row }">¥{{ formatPrice(row.revenue) }}</template>
          </el-table-column>
          <el-table-column align="center" label="趋势" min-width="200">
            <template #default="{ row }">
              <div class="mini-bar" :style="{ width: getRevenuePercent(row.revenue) + '%' }"></div>
            </template>
          </el-table-column>
        </el-table>
      </section>

      <!-- 会员增长趋势 -->
      <section class="panel">
        <h3 class="panel-title">近 30 天会员增长</h3>
        <el-table
          :data="stats.memberTrend || []"
          size="small"
          max-height="420"
          class="trend-table"
        >
          <el-table-column align="center" label="日期" prop="date" width="120" />
          <el-table-column align="center" label="新增会员" prop="count" width="100" />
          <el-table-column align="center" label="趋势" min-width="200">
            <template #default="{ row }">
              <div class="mini-bar mini-bar-member" :style="{ width: getMemberPercent(row.count) + '%' }"></div>
            </template>
          </el-table-column>
        </el-table>
      </section>
    </div>
  </div>
</template>

<script>
import axiosconf from '../../api/axiosconf'

export default {
  data() {
    return {
      loading: false,
      stats: {},
    }
  },
  computed: {
    roleDisplay() {
      const r = this.stats.memberByRole || {}
      return [
        { label: '城市代理', count: r.cityAgent || 0,     color: '#2EAD9E' },
        { label: '区县代理', count: r.districtAgent || 0, color: '#5CCABD' },
        { label: '经销商',   count: r.dealer || 0,        color: '#D4981A' },
        { label: '个人会员', count: r.member || 0,        color: '#1D7268' },
      ]
    },
    maxRevenue() {
      if (!this.stats.dailyTrend || this.stats.dailyTrend.length === 0) return 1
      return Math.max(...this.stats.dailyTrend.map(d => parseFloat(d.revenue) || 0), 1)
    },
    maxMemberCount() {
      if (!this.stats.memberTrend || this.stats.memberTrend.length === 0) return 1
      return Math.max(...this.stats.memberTrend.map(d => d.count || 0), 1)
    },
  },
  mounted() {
    this.fetchStats()
  },
  methods: {
    formatPrice(val) {
      if (val === null || val === undefined) return '0.00'
      return parseFloat(val).toFixed(2)
    },
    getRolePercent(count) {
      const total = this.stats.totalMembers || 1
      return Math.min((count / total) * 100, 100)
    },
    getRevenuePercent(revenue) {
      return Math.min((parseFloat(revenue) / this.maxRevenue) * 100, 100)
    },
    getMemberPercent(count) {
      return Math.min((count / this.maxMemberCount) * 100, 100)
    },
    async fetchStats() {
      this.loading = true
      try {
        const res = await axiosconf.applicationJson('/mall/admin/dashboard/stats')
        if (res.data && res.data.code === 0) {
          this.stats = res.data.data || {}
        }
      } catch (e) {
        console.error('获取统计数据失败', e)
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style lang="less" scoped>
  // ── Design Tokens（与 login/register 一致，本页内联） ──
  @primary-50:  #E6F7F5;
  @primary-100: #C2EDE7;
  @primary-300: #5CCABD;
  @primary-500: #2EAD9E;
  @primary-600: #258F83;
  @primary-700: #1D7268;
  @primary-800: #15544D;

  @amber-100: #FBE9C2;
  @amber-500: #D4981A;
  @amber-700: #92660A;

  @price-500: #E8450E;

  @gray-0:   #FFFFFF;
  @gray-50:  #FAFAF8;
  @gray-100: #F4F3F0;
  @gray-200: #E8E6E1;
  @gray-300: #DAD7D0;
  @gray-400: #B0ADA6;
  @gray-500: #8A8780;
  @gray-700: #4D4A43;
  @gray-800: #33312B;

  @font-display: 'Noto Serif SC', 'SimSun', serif;
  @font-body:    'Noto Sans SC', 'Microsoft YaHei', sans-serif;

  .dashboard {
    width: 100%;
    height: 100%;
    overflow-y: auto;
    background: @gray-50;
    font-family: @font-body;
    color: @gray-800;
  }
  .dashboard-inner {
    padding: 28px 32px 40px;
    max-width: 1440px;
    margin: 0 auto;
  }

  /* ─── 概览卡片 ─── */
  .stat-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;
    margin-bottom: 20px;
  }

  .stat-card {
    position: relative;
    background: @gray-0;
    border: 1px solid @gray-200;
    border-radius: 14px;
    padding: 22px 24px 20px;
    transition: box-shadow 200ms ease, transform 200ms ease;
    box-shadow: 0 1px 2px rgba(53, 51, 45, 0.03);

    &::before {
      content: '';
      position: absolute;
      top: 14px;
      left: 0;
      width: 3px;
      height: 28px;
      background: @primary-500;
      border-radius: 0 3px 3px 0;
    }
    &:hover {
      box-shadow:
        0 1px 2px rgba(53, 51, 45, 0.04),
        0 12px 28px rgba(46, 173, 158, 0.08);
      transform: translateY(-1px);
    }

    &.accent-revenue {
      &::before { background: @price-500; }
      .stat-value { color: @price-500; }
    }
    &.accent-warn {
      &::before { background: @amber-500; }
      .stat-value { color: @amber-700; }
    }
  }

  .stat-label {
    font-size: 13px;
    color: @gray-500;
    letter-spacing: 1px;
    margin-bottom: 10px;
  }
  .stat-value {
    font-family: @font-display;
    font-size: 30px;
    font-weight: 600;
    color: @gray-800;
    line-height: 1.2;
    letter-spacing: 1px;
  }
  .stat-sub {
    margin-top: 6px;
    font-size: 12px;
    color: @gray-400;
    letter-spacing: 0.5px;
  }

  /* ─── 数据面板 ─── */
  .panel {
    background: @gray-0;
    border: 1px solid @gray-200;
    border-radius: 16px;
    padding: 24px 28px 28px;
    margin-bottom: 16px;
    box-shadow: 0 1px 2px rgba(53, 51, 45, 0.03);
  }
  .panel-title {
    font-family: @font-display;
    font-size: 16px;
    font-weight: 600;
    color: @gray-800;
    letter-spacing: 1.5px;
    margin: 0 0 20px;
    padding-bottom: 12px;
    border-bottom: 1px solid @gray-100;
    display: flex;
    align-items: center;
    &::before {
      content: '';
      width: 3px;
      height: 14px;
      background: @primary-500;
      margin-right: 10px;
      border-radius: 2px;
    }
  }

  /* ─── 角色分布 ─── */
  .role-bars {
    display: flex;
    flex-direction: column;
    gap: 14px;
  }
  .role-item {
    display: flex;
    align-items: center;
  }
  .role-label {
    width: 80px;
    font-size: 13px;
    color: @gray-700;
    letter-spacing: 0.5px;
  }
  .role-bar-wrap {
    flex: 1;
    height: 10px;
    background: @gray-100;
    border-radius: 5px;
    overflow: hidden;
    margin: 0 14px;
  }
  .role-bar {
    height: 100%;
    border-radius: 5px;
    transition: width 0.6s ease;
    min-width: 4px;
  }
  .role-count {
    width: 48px;
    text-align: right;
    font-family: @font-display;
    font-size: 14px;
    font-weight: 600;
    color: @gray-800;
  }

  /* ─── 趋势表 ─── */
  .trend-table {
    :deep(.el-table) {
      --el-table-border-color: @gray-100;
      --el-table-header-bg-color: @gray-50;
      --el-table-row-hover-bg-color: @primary-50;
      background: transparent;
    }
    :deep(.el-table th.el-table__cell) {
      background: @gray-50;
      color: @gray-700;
      font-weight: 500;
      font-size: 12px;
      letter-spacing: 1px;
      border-bottom-color: @gray-200;
    }
    :deep(.el-table td.el-table__cell) {
      color: @gray-800;
      font-size: 13px;
      border-bottom-color: @gray-100;
    }
  }

  .mini-bar {
    height: 8px;
    background: linear-gradient(90deg, @primary-300, @primary-600);
    border-radius: 4px;
    transition: width 0.3s ease;
    min-width: 4px;
  }
  .mini-bar-member {
    background: linear-gradient(90deg, @amber-100, @amber-500);
  }

  /* ─── 响应式 ─── */
  @media (max-width: 1100px) {
    .stat-grid { grid-template-columns: repeat(2, 1fr); }
  }
  @media (max-width: 640px) {
    .dashboard-inner { padding: 16px; }
    .stat-grid { grid-template-columns: 1fr; }
    .panel { padding: 18px 16px 20px; }
  }
</style>
